import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Edit2, Trash2, AlertTriangle, Plus,
  Umbrella, Gift, Star, Flag, Sun, Send, Camera, Compass, Home, Heart, Truck, Target
} from 'react-feather';
import { apiClient } from '../../shared/api/client';
import { parseAmount } from '../../shared/utils/amount';

interface SavingsGoal {
  id: number;
  kind: 'pot' | 'kassa';
  name: string;
  targetAmount: number | null;
  manualBalance: number | null;
  missingToTarget: number | null;
  categoryId: number;
  categoryName: string;
  color?: string;
  icon?: string;
  monthlyTarget: number | null;
  built: number;
  spent: number;
  kassa: number;
  hasLinkedAccount: boolean;
}

const AVAILABLE_ICONS = [
  { name: 'umbrella', component: Umbrella },
  { name: 'gift', component: Gift },
  { name: 'star', component: Star },
  { name: 'flag', component: Flag },
  { name: 'sun', component: Sun },
  { name: 'send', component: Send },
  { name: 'camera', component: Camera },
  { name: 'compass', component: Compass },
  { name: 'home', component: Home },
  { name: 'heart', component: Heart },
  { name: 'truck', component: Truck },
  { name: 'target', component: Target },
];

const COLORS = ['#0EA5E9', '#EC4899', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#14B8A6', '#6366F1', '#F97316', '#84CC16', '#3B82F6', '#06B6D4'];

// Manage savings goals (kassor): each goal is a spending category whose
// budget is a saved-up pot instead of a monthly cap.
const SavingsGoalsPage: React.FC = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<SavingsGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: '', icon: 'umbrella', color: '#0EA5E9', monthlyTarget: '',
    kind: 'kassa' as 'kassa' | 'pot', targetAmount: '', manualBalance: ''
  });
  const [saving, setSaving] = useState(false);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' kr';

  const loadGoals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/goals');
      setGoals(((response as any).goals || []) as SavingsGoal[]);
    } catch (err) {
      console.error('Load goals error:', err);
      setError('Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGoals(); }, []);

  const getIconComponent = (iconName?: string) => {
    const icon = AVAILABLE_ICONS.find(i => i.name === iconName);
    return icon ? icon.component : Target;
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: '', icon: 'umbrella', color: '#0EA5E9', monthlyTarget: '', kind: 'kassa', targetAmount: '', manualBalance: '' });
    setShowModal(true);
  };

  const openEdit = (goal: SavingsGoal) => {
    setEditingId(goal.id);
    setForm({
      name: goal.name,
      icon: goal.icon || 'target',
      color: goal.color || '#0EA5E9',
      monthlyTarget: goal.monthlyTarget != null ? String(goal.monthlyTarget) : '',
      kind: goal.kind || 'kassa',
      targetAmount: goal.targetAmount != null ? String(goal.targetAmount) : '',
      manualBalance: goal.manualBalance != null ? String(goal.manualBalance) : '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        icon: form.icon,
        color: form.color,
        kind: form.kind,
        monthlyTarget: form.monthlyTarget.trim() ? parseAmount(form.monthlyTarget) : null,
        targetAmount: form.targetAmount.trim() ? parseAmount(form.targetAmount) : null,
        manualBalance: form.manualBalance.trim() ? parseAmount(form.manualBalance) : null,
      };
      if (editingId) {
        await apiClient.put(`/goals/${editingId}`, payload);
      } else {
        await apiClient.post('/goals', payload);
      }
      setShowModal(false);
      await loadGoals();
    } catch (err) {
      console.error('Save goal error:', err);
      alert('Kunde inte spara sparmålet.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goal: SavingsGoal) => {
    if (!window.confirm(`Ta bort sparmålet "${goal.name}"? Kategorin ${goal.categoryName} tas bara bort om den är oanvänd — annars blir den en vanlig kategori.`)) {
      return;
    }
    try {
      await apiClient.delete(`/goals/${goal.id}`);
      await loadGoals();
    } catch (err) {
      console.error('Delete goal error:', err);
      alert('Kunde inte ta bort sparmålet.');
    }
  };

  const header = (
    <div className="page-header bg-white shadow-sm border-b border-gray-200">
      <div className="page-header-content px-4 py-4">
        <div className="flex items-center">
          <button onClick={() => navigate('/settings')} className="mr-4">
            <span className="text-xl">‹</span>
          </button>
          <h1 className="text-xl font-bold text-gray-900">Sparmål</h1>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Laddar sparmål...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="page-content px-4 py-6">
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold text-red-600 mb-2">Kunde inte ladda sparmål</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadGoals}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {header}
      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">
          Kassor för sånt som inte är månadskostnader — semester, presenter, större inköp.
          Köp i sparmålets kategori räknas mot kassan istället för ett månadstak.
        </p>

        <div className="mb-6">
          {goals.length > 0 ? (
            goals.map(goal => {
              const IconComponent = getIconComponent(goal.icon);
              return (
                <div key={goal.id} className="bg-white rounded-xl shadow-sm p-4 mb-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                        <IconComponent size={20} style={{ color: goal.color || '#0EA5E9' }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{goal.name}</h3>
                        <p className="text-xs text-gray-500">
                          {formatAmount(goal.kassa)}{goal.targetAmount ? ` av ${formatAmount(goal.targetAmount)}` : goal.kind === 'pot' ? '' : ' i kassan'}
                          {goal.monthlyTarget ? ` · fylls på ${formatAmount(goal.monthlyTarget)}/mån` : ''}
                          {goal.kind === 'kassa' && goal.categoryName ? ` · kategori ${goal.categoryName}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEdit(goal)}
                        className="text-gray-900 hover:text-gray-600 transition-colors p-2"
                        title="Redigera sparmål"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(goal)}
                        className="text-gray-900 hover:text-gray-600 transition-colors p-2"
                        title="Ta bort sparmål"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Target size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga sparmål ännu</h3>
              <p className="text-gray-600">Skapa en kassa för t.ex. semester eller presenter</p>
            </div>
          )}
        </div>

        <button
          onClick={openAdd}
          className="w-full bg-white border-2 border-dashed border-blue-600 text-blue-600 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
        >
          <Plus size={16} className="inline mr-1 -mt-0.5" />Lägg till sparmål
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingId ? 'Redigera sparmål' : 'Lägg till sparmål'}
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Namn</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="T.ex. Semesterkassa"
                autoFocus
              />
              {!editingId && (
                <p className="text-xs text-gray-400 mt-1">En kategori med samma namn skapas för köpen.</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj ikon</label>
              <div className="grid grid-cols-6 gap-2">
                {AVAILABLE_ICONS.map(icon => {
                  const IconComp = icon.component;
                  return (
                    <button
                      key={icon.name}
                      onClick={() => setForm({ ...form, icon: icon.name })}
                      className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-center ${
                        form.icon === icon.name
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <IconComp size={20} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj färg</label>
              <div className="grid grid-cols-6 gap-2">
                {COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setForm({ ...form, color })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      form.color === color ? 'border-gray-900 scale-110' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {!editingId && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vad är det?</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setForm({ ...form, kind: 'kassa' })}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm text-left ${form.kind === 'kassa' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}
                  >
                    <span className="font-semibold block">Kassa</span>
                    <span className="text-xs text-gray-500">Köp räknas mot den</span>
                  </button>
                  <button
                    onClick={() => setForm({ ...form, kind: 'pot' })}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm text-left ${form.kind === 'pot' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}
                  >
                    <span className="font-semibold block">Sparpott</span>
                    <span className="text-xs text-gray-500">Bara saldo och mål</span>
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Målsumma (kr, valfritt)</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.targetAmount}
                onChange={e => setForm({ ...form, targetAmount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="T.ex. 15 000"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Saldo idag (kr)</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.manualBalance}
                onChange={e => setForm({ ...form, manualBalance: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Vad står på kontot nu?"
              />
              <p className="text-xs text-gray-400 mt-1">
                ISK- och fondkonton ingår inte i bankkopplingen, så saldot skriver du in själv när du vill stämma av.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Påfyllning per månad (kr, valfritt)</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.monthlyTarget}
                onChange={e => setForm({ ...form, monthlyTarget: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="T.ex. 1000"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavingsGoalsPage;
